import { useState, useEffect, useCallback } from 'react';
import { useServerInfiniteScroll } from '../hooks/useServerInfiniteScroll';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback } from './ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Search, LogOut, User, Radio, Edit, Plus, Eye, EyeOff } from 'lucide-react';
import { Station } from '../data/mockData';
import { EditStationDrawer } from './EditStationDrawer';
import { CreateStationDrawer } from './CreateStationDrawer';
import { ResetPasswordDialog } from './ResetPasswordDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { useUpdateStationCredentialsMutation, useListStationsPaginatedQuery } from '../../store/api';
import { toast } from 'sonner';
import { NotificationBell } from './NotificationBell';

interface StationListScreenProps {
  currentUsername: string;
  onSelectStation: (station: Station) => void;
  onUpdateStation: (stationId: string | number, station: Omit<Station, 'id' | 'channelCount'>) => void;
  onDeleteStation: (stationId: string | number) => void;
  onCreateStation: (station: { name: string; status: 'Active' | 'Suspended' }) => void;
  onLogout: () => void;
}

export function StationListScreen({
  currentUsername,
  onSelectStation,
  onUpdateStation,
  onDeleteStation,
  onCreateStation,
  onLogout,
}: StationListScreenProps) {
  const displayName = currentUsername || 'User';
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [editingStation, setEditingStation] = useState<Station | null>(null);
  const [isCreatingStation, setIsCreatingStation] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [resetStation, setResetStation] = useState<Station | null>(null);
  const [visiblePasswords, setVisiblePasswords] = useState<Record<string, boolean>>({});
  const [updateCredentials, { isLoading: isSavingCredentials }] = useUpdateStationCredentialsMutation();

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Server-side paginated stations
  const [pageOffset, setPageOffset] = useState(0);
  const PAGE_LIMIT = 20;

  const { data: stationsData, isFetching } = useListStationsPaginatedQuery({
    offset: pageOffset,
    limit: PAGE_LIMIT,
    search: debouncedSearch || undefined,
  });

  const stations: Station[] = (stationsData?.items ?? []).map((s: any) => ({
    ...s,
    id: String(s.id),
    channelCount: s.channelCount ?? s.channel_count ?? 0,
    loginUsername: s.loginUsername ?? s.login_username ?? '',
    loginPassword: s.loginPassword ?? s.login_password ?? '',
  }));

  const loadMore = useCallback(() => {
    if (stationsData?.hasMore && !isFetching) {
      setPageOffset((prev) => prev + PAGE_LIMIT);
    }
  }, [stationsData?.hasMore, isFetching]);

  const { sentinelRef } = useServerInfiniteScroll({
    hasMore: stationsData?.hasMore ?? false,
    isFetching,
    onLoadMore: loadMore,
  });

  // Reset pagination on search change
  useEffect(() => {
    setPageOffset(0);
  }, [debouncedSearch]);

  const handleUpdatePassword = async (password: string) => {
    if (!resetStation) {
      return;
    }

    const numericStationId = Number(resetStation.id);
    if (!Number.isFinite(numericStationId)) {
      return;
    }

    try {
      await updateCredentials({
        stationId: numericStationId,
        password,
      }).unwrap();
      toast.success('Password updated');
      setResetStation(null);
    } catch (error) {
      toast.error('Unable to update password');
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="border-b border-gray-800 bg-gray-950">
        <div className="px-4 sm:px-6 py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Radio className="size-6 text-blue-500 shrink-0" />
            <span className="text-xl font-bold text-white hidden sm:inline">Radio Service</span>
            <Badge className="ml-2 bg-purple-900 text-purple-200 hover:bg-purple-900 hidden sm:inline-flex">
              {displayName}
            </Badge>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <NotificationBell global variant="dark" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 text-gray-300 hover:text-white hover:bg-gray-800">
                  <Avatar className="size-8">
                    <AvatarFallback className="bg-purple-600 text-white">
                      <User className="size-4" />
                    </AvatarFallback>
                  </Avatar>
                  <span>{displayName}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-gray-900 border-gray-800">
                <DropdownMenuItem onClick={onLogout} className="text-gray-300 cursor-pointer hover:bg-gray-800 focus:bg-gray-800">
                  <LogOut className="mr-2 size-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-4 sm:p-8">
        <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">Stations</h1>
            <p className="text-gray-400">
              Select a station to manage its channels and content
            </p>
          </div>
          <Button
            onClick={() => setIsCreatingStation(true)}
            className="gap-2 bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto"
          >
            <Plus className="size-4" />
            Create Station
          </Button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-500" />
            <Input
              placeholder="Search by name or code"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
            />
          </div>
        </div>

        {/* Stations Table */}
        {stations.length === 0 && !isFetching ? (
          <div className="text-center py-12 bg-gray-800 rounded-lg border border-gray-700">
            <p className="text-gray-400 text-lg">No stations found.</p>
          </div>
        ) : (
          <div className="rounded-lg border border-gray-800 bg-gray-900 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-gray-800 hover:bg-transparent">
                  <TableHead className="text-gray-400 hidden sm:table-cell">Serial No</TableHead>
                  <TableHead className="text-gray-400">Station Name</TableHead>
                  <TableHead className="text-gray-400">Status</TableHead>
                  <TableHead className="text-gray-400 hidden md:table-cell">Username</TableHead>
                  <TableHead className="text-gray-400 hidden md:table-cell">Password</TableHead>
                  <TableHead className="text-gray-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stations.map((station, index) => {
                  const isSuspended = station.status === 'Suspended';

                  return (
                    <TableRow
                      key={station.id}
                      className={`border-gray-800 ${
                        isSuspended ? 'opacity-50' : 'hover:bg-gray-800'
                      }`}
                    >
                      <TableCell className="text-gray-400 font-mono hidden sm:table-cell">
                        {index + 1}
                      </TableCell>
                      <TableCell className="text-white font-medium">
                        <div className="flex items-center gap-2">
                          <Radio className="size-4 text-blue-500" />
                          {station.name}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={station.status === 'Active' ? 'default' : 'secondary'}
                          className={
                            station.status === 'Active'
                              ? 'bg-green-600 hover:bg-green-700 text-white'
                              : 'bg-gray-600 hover:bg-gray-700 text-gray-300'
                          }
                        >
                          {station.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-gray-300 hidden md:table-cell">
                        {station.loginUsername ?? 'Not set'}
                      </TableCell>
                      <TableCell className="text-gray-300 hidden md:table-cell">
                        <div className="flex items-center gap-2">
                          <span className="font-mono">
                            {station.loginPassword
                              ? (visiblePasswords[String(station.id)] ? station.loginPassword : '••••••••')
                              : 'Not set'}
                          </span>
                          {station.loginPassword && (
                            <button
                              type="button"
                              onClick={() =>
                                setVisiblePasswords((prev) => ({
                                  ...prev,
                                  [String(station.id)]: !prev[String(station.id)],
                                }))
                              }
                              className="rounded-md border border-gray-700 bg-gray-800 p-1 text-gray-200 hover:text-white"
                              aria-label={visiblePasswords[String(station.id)] ? 'Hide password' : 'Show password'}
                            >
                              {visiblePasswords[String(station.id)] ? (
                                <EyeOff className="size-4" />
                              ) : (
                                <Eye className="size-4" />
                              )}
                            </button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1 sm:gap-2 flex-wrap">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingStation(station)}
                            className="gap-1 text-gray-400 hover:text-white hover:bg-gray-800"
                          >
                            <Edit className="size-4" />
                            <span className="hidden sm:inline">Edit</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setResetStation(station)}
                            className="gap-1 text-gray-400 hover:text-white hover:bg-gray-800 hidden sm:inline-flex"
                          >
                            Reset Password
                          </Button>
                          <Button
                            size="sm"
                            disabled={isSuspended}
                            onClick={() => onSelectStation(station)}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                          >
                            <span className="hidden sm:inline">Manage Station</span>
                            <span className="sm:hidden">Manage</span>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
        {(stationsData?.hasMore ?? false) && (
          <div ref={sentinelRef} className="flex justify-center py-6">
            <div className="text-sm text-gray-500">Loading more...</div>
          </div>
        )}
      </main>

      {/* Edit Station Drawer */}
      {editingStation && (
        <EditStationDrawer
          isOpen={true}
          station={editingStation}
          onSave={(data) => {
            onUpdateStation(editingStation.id, data);
            setEditingStation(null);
          }}
          onDelete={() => {
            setShowDeleteConfirm(true);
          }}
          onClose={() => setEditingStation(null)}
        />
      )}

      {/* Create Station Drawer */}
      {isCreatingStation && (
        <CreateStationDrawer
          isOpen={true}
          onSave={onCreateStation}
          onClose={() => setIsCreatingStation(false)}
        />
      )}

      {resetStation && (
        <ResetPasswordDialog
          isOpen={true}
          station={resetStation}
          isSaving={isSavingCredentials}
          onClose={() => setResetStation(null)}
          onSave={handleUpdatePassword}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent className="bg-gray-900 border-gray-800 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Delete</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Delete station "{editingStation?.name}"? This will soft-delete its channels and tracks.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-800 border-gray-700 text-white hover:bg-gray-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (editingStation) {
                  onDeleteStation(editingStation.id);
                  setEditingStation(null);
                }
                setShowDeleteConfirm(false);
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}